import React, { useCallback, useEffect, useMemo } from "react"
import { IconInfo, IconWarn } from "dash-styled"
import { getValue, hasValue } from "../../../../../Models/Client/FetchState"
import { AppStatusBannerTemplate } from "./AppStatusBannerTemplate"
import { useAssumedRoles } from "../../../../../Hooks/useAssumedRoles"
import { useAppInstance } from "../../../../../Hooks/useAppInstance"
import { hasAppPermission } from "../../../../../Models/Client/ProjectRole"
import { useAIModelsForApp } from "../../../../../Hooks/useAIModelsForApp"
import { useAIModelDeployment } from "../../../../../Hooks/useAIModelDeployment"
import { AIModel, getModelName } from "../../../../../Models/API/AIModel"
import { metaAI } from "neql"

const assignedMetaAIApps = [
    metaAI.Meta_Ai_Assignment_Enum.ActiveLearning,
    metaAI.Meta_Ai_Assignment_Enum.Prelabel
]

interface AppAIStatusBannerProps {
    appID: string
}

export function AppAIStatusBanner({ appID }: AppAIStatusBannerProps) {
    const instanceFetch = useAppInstance(appID)
    const rolesFetch = useAssumedRoles(appID)
    const {
        data: modelList,
        refetch: refetchModels,
        startPolling,
        stopPolling
    } = useAIModelsForApp(appID)
    const [onRequestPrediction, isRequestingDeployment] =
        useAIModelDeployment(appID)

    const offlineModels = useMemo<AIModel[]>(() => {
        if (
            !hasAppPermission(getValue(rolesFetch) ?? [], "ai:update") ||
            !modelList?.meta_ai_model ||
            modelList.meta_ai_model.length === 0
        ) {
            return []
        }
        return modelList.meta_ai_model.filter(
            model =>
                model.apps.some(
                    app =>
                        assignedMetaAIApps.includes(app.assigned) && app.active
                ) &&
                !["ONLINE", "FAILED"].includes(
                    model.deployment?.status ?? ""
                ) &&
                model.deployment?.status === model.deployment?.target_status
        )
    }, [modelList, rolesFetch])

    const deploymentFailedModels = useMemo<AIModel[]>(() => {
        if (
            !hasAppPermission(getValue(rolesFetch) ?? [], "ai:update") ||
            !modelList?.meta_ai_model ||
            modelList.meta_ai_model.length === 0
        ) {
            return []
        }
        return modelList.meta_ai_model.filter(
            model =>
                model.apps.some(
                    app =>
                        assignedMetaAIApps.includes(app.assigned) && app.active
                ) && model.deployment?.status === "FAILED"
        )
    }, [modelList, rolesFetch])

    const deployingModels = useMemo<AIModel[]>(() => {
        if (
            !hasAppPermission(getValue(rolesFetch) ?? [], "ai:update") ||
            !modelList?.meta_ai_model ||
            modelList.meta_ai_model.length === 0
        ) {
            return []
        }
        return modelList.meta_ai_model.filter(
            model =>
                model.apps.some(
                    app =>
                        assignedMetaAIApps.includes(app.assigned) && app.active
                ) &&
                !["ONLINE", "FAILED"].includes(
                    model.deployment?.status ?? ""
                ) &&
                model.deployment?.status !== model.deployment?.target_status
        )
    }, [modelList, rolesFetch])

    const handleRequestPrediction = useCallback(async () => {
        if (offlineModels.length > 0) {
            await onRequestPrediction(offlineModels.map(({ id }) => id))
            await refetchModels()
            startPolling(5000)
        }
    }, [offlineModels, onRequestPrediction, refetchModels, startPolling])

    const areModelsOnline =
        offlineModels.length === 0 &&
        deploymentFailedModels.length === 0 &&
        deployingModels.length === 0

    useEffect(() => {
        if (areModelsOnline) {
            stopPolling()
        }
    }, [areModelsOnline, stopPolling])

    if (!hasValue(rolesFetch) || !hasValue(instanceFetch) || areModelsOnline) {
        return null
    }

    return (
        <AppStatusBannerTemplate
            bgColor={
                deploymentFailedModels.length > 0
                    ? "#d43155"
                    : deployingModels.length > 0
                    ? "#FFBA00"
                    : "#86798A"
            }
            title={
                offlineModels.length > 1
                    ? `The AI models are ${
                          deployingModels.length > 0 ? "deploying" : "offline"
                      }`
                    : `The AI model is ${
                          deployingModels.length > 0 ? "deploying" : "offline"
                      }`
            }
            message={
                deploymentFailedModels.length > 0
                    ? `Deployment failed for the associated model${
                          deploymentFailedModels.length > 1 ? "s" : ""
                      }: ${deploymentFailedModels.map(getModelName).join(", ")}`
                    : deployingModels.length > 0
                    ? `Deploying associated model${
                          deployingModels.length > 1 ? "s" : ""
                      }: ${deployingModels.map(getModelName).join(", ")}`
                    : `Consider activating the associated model${
                          offlineModels.length > 1 ? "s" : ""
                      }: ${offlineModels.map(getModelName).join(", ")}`
            }
            icon={
                deployingModels.length > 0 ? (
                    <IconInfo size="xl" />
                ) : (
                    <IconWarn size="xl" />
                )
            }
            action={
                offlineModels.length === 0
                    ? undefined
                    : isRequestingDeployment
                    ? ["Activate", undefined]
                    : ["Activate", handleRequestPrediction]
            }
        />
    )
}
